package mapsearch.buildings

import apiclient.FormationClient
import apiclient.geoobjects.BuildingAndFloorTags
import apiclient.geoobjects.BuildingFormat
import apiclient.geoobjects.LatLon
import apiclient.geoobjects.pointCoordinates
import apiclient.tags.floorLevel
import apiclient.tags.getUniqueTag
import auth.ApiUserStore
import com.jillesvangurp.geo.GeoGeometry
import com.jillesvangurp.geojson.Geometry
import data.heatmaplayer.ActiveHeatmapLayerDefinitionStore.Companion.json
import dev.fritz2.core.RootStore
import koin.koinCtx
import koin.withKoin
import kotlinx.coroutines.Job
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.builtins.nullable
import kotlinx.serialization.builtins.serializer
import layercache.floorsForBuilding
import maplibreGL.FormationMap
import maplibreGL.toLatLon
import model.Building
import model.Floor
import mu.KotlinLogging

private val currentBuildingLogger = KotlinLogging.logger("CurrentBuildingsStore")

class CurrentBuildingsStore : RootStore<Map<String, Building>>(emptyMap(), Job()) {

    private val buildingLookUpStore: BuildingLookUpStore by koinCtx.inject()

    fun reset() {
        update(emptyMap())
    }

    val forceRefreshFloorData = handle<String> { current, buildingId ->
        (current[buildingId] ?: buildingLookUpStore.current[buildingId])?.let { building ->
            currentBuildingLogger.info { "Force refresh floor Data for ${building.buildingName}" }
            val mutable = current.toMutableMap()
            mutable[buildingId] = building.copy(
                floorData = fetchFloorData(buildingId),
            )
            buildingLookUpStore.updateBuildings(mutable.toMap())
            mutable.toMap()
        } ?: current
    }

    private suspend fun fetchFloorData(buildingId: String): MutableMap<Double, List<Floor>> {
        withKoin {
            val formationClient: FormationClient = get()
            val apiUserStore: ApiUserStore = get()
            val groupIds = apiUserStore.current.apiUser?.groups?.map { it.groupId }
            return groupIds?.let {
                val floorData = mutableMapOf<Double, List<Floor>>()
                val result = formationClient.floorsForBuilding(groupIds = groupIds, buildingId = buildingId)
                result?.forEach { (floor, units) ->
                    val level = floor.hit.tags.floorLevel
                    if (level != null) {
                        floorData[level] = floorData[level].orEmpty() + Floor(floor.hit, units.hits.map { it.hit })
                    }
                }
                floorData
            } ?: mutableMapOf()
        }
    }

    val setBuildings = handle<List<Building>> { current, buildings ->
        buildings.associate { building ->
            building.buildingId to (current[building.buildingId] ?: run {
                // Fetch floorData and update Building with it
                val newFloorData = fetchFloorData(building.buildingId)
                building.copy(
                    activeFloorLevel = building.activeFloorLevel ?: building.defaultFloorLevel,
                    activeFloorIds = newFloorData[building.activeFloorLevel ?: building.defaultFloorLevel].orEmpty()
                        .map { it.floor.id },
                )
            })
        }
    }

    val updateBuildings = handle<Map<String, Building>> { current, newBuildings ->
        val mutable = current.toMutableMap()
        newBuildings.entries.forEach { (buildingId, building) ->
            mutable[buildingId] = building
        }
        mutable.toMap()
    }

    /**
     *  Updates the activeFloorsStore with the active floorIds of all buildings
     */
    private fun syncActiveFloors() {
        withKoin {
            val activeFloorsStore: ActiveFloorsStore = get()
            activeFloorsStore.update(current.values.toList().getActiveFloorIds)
        }
    }

    fun pointWithinFloor(point: LatLon? = null): String? {
        withKoin {
            val formationMap: FormationMap = get()

            val floors = current.values.flatMap { building ->
                building.getActiveFloors.orEmpty()
            }
            val currentCenter = point ?: formationMap.getCenter().toLatLon()
            val floorList = floors.filter { floor ->
                when (floor.floor.tags.getUniqueTag(BuildingAndFloorTags.BuildingFormat)
                    ?.let { BuildingFormat.valueOf(it) }) {
                    BuildingFormat.Image -> {
                        val (tlLon, tlLat) = json.decodeFromString(
                            ListSerializer(Double.serializer().nullable),
                            floor.floor.tags.getUniqueTag(BuildingAndFloorTags.TopLeft) ?: "",
                        ).filterNotNull()
                        val (trLon, trLat) = json.decodeFromString(
                            ListSerializer(Double.serializer().nullable),
                            floor.floor.tags.getUniqueTag(BuildingAndFloorTags.TopRight) ?: "",
                        ).filterNotNull()
                        val (blLon, blLat) = json.decodeFromString(
                            ListSerializer(Double.serializer().nullable),
                            floor.floor.tags.getUniqueTag(BuildingAndFloorTags.BottomLeft) ?: "",
                        ).filterNotNull()
                        val (brLon, brLat) = json.decodeFromString(
                            ListSerializer(Double.serializer().nullable),
                            floor.floor.tags.getUniqueTag(BuildingAndFloorTags.BottomRight) ?: "",
                        ).filterNotNull()

                        GeoGeometry.polygonContains(
                            pointCoordinates = doubleArrayOf(currentCenter.lon, currentCenter.lat),
                            polygonCoordinatesPoints = arrayOf(
                                arrayOf(
                                    doubleArrayOf(tlLon, tlLat),
                                    doubleArrayOf(trLon, trLat),
                                    doubleArrayOf(brLon, brLat),
                                    doubleArrayOf(blLon, blLat),
                                ),
                            ),
                        )
                    }

                    BuildingFormat.GeoJson -> {
                        floor.floor.geometry?.let { geo ->
                            when (geo) {
                                is Geometry.Polygon -> {
                                    geo.coordinates?.let {
                                        GeoGeometry.Companion.polygonContains(
                                            currentCenter.pointCoordinates(),
                                            it,
                                        )
                                    } == true
                                }

                                is Geometry.MultiPolygon -> {
                                    geo.coordinates?.map {
                                        GeoGeometry.Companion.polygonContains(
                                            currentCenter.pointCoordinates(),
                                            it,
                                        )
                                    }?.reduce { a, b -> a || b } == true
                                }

                                else -> {
                                    false
                                }
                            }
                        } ?: false
                    }

                    else -> false
                }
            }

            return if (floorList.isNotEmpty()) {
                floorList.firstOrNull()?.floor?.id
            } else null

        }
    }

    private fun pointWithinUnit(): String? {
        withKoin {
            val formationMap: FormationMap = get()

            val units = current.values.flatMap { building ->
                building.getActiveFloors.orEmpty().flatMap { floor -> floor.units.orEmpty() }
            }
            val currentCenter = formationMap.getCenter().toLatLon()

            val unitList = units.filter { unit ->
                unit.geometry?.let { geo ->
                    when (geo) {
                        is Geometry.Polygon -> {
                            geo.coordinates?.let { c ->
                                GeoGeometry.Companion.polygonContains(
                                    currentCenter.pointCoordinates(),
                                    c,
                                )
                            } == true
                        }

                        is Geometry.MultiPolygon -> {
                            geo.coordinates?.map { c ->
                                GeoGeometry.Companion.polygonContains(
                                    currentCenter.pointCoordinates(),
                                    c,
                                )
                            }
                                ?.reduce { a, b -> a || b } == true
                        }

                        else -> {
                            false
                        }
                    }
                } ?: false
            }

            return if (unitList.isNotEmpty()) {
                unitList.firstOrNull()?.id
            } else null

        }
    }

    fun getConnectedToId(): String? {
        val pointWithinFloor = pointWithinFloor()
        return if (!pointWithinFloor.isNullOrBlank()) {
            pointWithinUnit() ?: pointWithinFloor
        } else null
    }

    init {
        syncActiveFloors()
        data handledBy {
            syncActiveFloors()
        }
    }
}
